// Right-sizes image URLs so the storefront never ships full-resolution originals
// where a smaller file will do (big LCP + bandwidth win). Centralised here so the
// whole app requests appropriately-sized images and so switching to the platform's
// own imgproxy later is a one-file change.
//
// Today only Shopify CDN URLs (imported catalogs) can be resized — they support an
// on-the-fly `width` transform. Every other host (platform uploads / S3) is served
// as stored, so `resizable` is the ONE place that says which hosts we can ask for
// a different size: sizedImage and imageSrcSet must agree about that, or a srcset
// would offer several candidates that are all the same bytes under different width
// descriptors and the browser would pick by a number that means nothing.

/** Whether this host can serve the same image at a caller-chosen width. */
function resizable(src: string): boolean {
  return src.includes("cdn.shopify.com")
}

export function sizedImage(src: string | undefined | null, width: number): string {
  if (!src) return ""
  if (!resizable(src)) return src
  const sep = src.includes("?") ? "&" : "?"
  return `${src}${sep}width=${width}`
}

/** The candidate widths a srcset offers. ONE ladder for every surface: a rung a
 *  given `sizes` can never pick costs a few bytes of a string built at runtime,
 *  while a second ladder is a second place for the top rung to drift.
 *
 *  The top rung is 1200 because that is what the PDP requested unconditionally
 *  before srcset existed — keeping it means a 2x-DPR desktop, the case that was
 *  already right, is unchanged, and every narrower or 1x viewport gets the win.
 *  The 200/300 rungs exist for product CARDS, which are a quarter of a page wide.
 *  Measured on a real 1254px source (Shopify CDN, WebP): 200 → 24KB · 400 → 78KB
 *  · 600 → 161KB · 800 → 265KB · 1200 → 464KB. */
export const IMAGE_WIDTHS = [200, 300, 400, 600, 800, 1200] as const

/** The `src` fallback width: the top rung, so a browser with no srcset support
 *  gets exactly what it got before srcset existed. */
export const IMAGE_MAX_WIDTH: number = IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1]!

/** A `srcset` for an image, or undefined when the host cannot resize — undefined
 *  so the caller can bind it straight to the attribute and have Vue omit it. A
 *  one-candidate srcset would be strictly worse than none: it costs bytes and
 *  tells the browser a width we have not actually honoured. */
export function imageSrcSet(
  src: string | undefined | null,
  widths: readonly number[] = IMAGE_WIDTHS,
): string | undefined {
  if (!src || !resizable(src)) return undefined
  return widths.map((w) => `${sizedImage(src, w)} ${w}w`).join(", ")
}

/** `sizes` for the PDP media column. Plain lengths and media queries only — no
 *  min()/calc(), which older Safari drops from a sizes attribute, taking the
 *  whole descriptor with it. The column is half of `.dk-container` (max-width
 *  `--dk-page-width`, 1200px by default, 1400px on the widest shipped theme)
 *  minus the 3rem grid gap, and the grid collapses to one column at 900px —
 *  which is why that breakpoint is the same number as the one in styles.css. */
export const PDP_IMAGE_SIZES = "(max-width: 900px) 100vw, (max-width: 1400px) 46vw, 660px"

/** The widest a `.dk-container` gets: `--dk-page-width` defaults to 1200px and the
 *  widest shipped theme sets 1400. Used only to stop `sizes` growing with the
 *  viewport past the point the layout itself stops growing. */
const WIDEST_PAGE = 1400
/** `.dk-container` horizontal padding (1.25rem each side) and `.dk-grid`'s gap. */
const PAGE_PAD = 40
const GRID_GAP = 24

/** `sizes` for a product card in a `.dk-grid`.
 *
 *  The grid is `repeat(var(--cols, 4), 1fr)` above 900px and 2 columns below, and
 *  a `sizes` attribute cannot read a custom property — so the counts are passed in
 *  by the one section that lets a seller change them (`featured-collection`) and
 *  every other caller gets the grid's own defaults. Deliberately IGNORES the
 *  container padding and gaps in the vw terms, which makes it slightly generous:
 *  erring large costs one rung of bytes, erring small renders a soft photo, and
 *  only one of those looks broken. */
export function cardImageSizes(cols = 4, colsMobile = 2): string {
  // `|| 1` before the clamp, not after: a column count comes out of a
  // seller-authored theme document via Number(), so NaN is reachable — and
  // Math.max(1, NaN) is NaN, which would emit `NaNvw` and void the whole
  // attribute. (Found by its own test, not by reading.)
  const c = Math.max(1, Math.round(cols) || 1)
  const m = Math.max(1, Math.round(colsMobile) || 1)
  const cap = Math.ceil((WIDEST_PAGE - PAGE_PAD - (c - 1) * GRID_GAP) / c)
  return `(max-width: 900px) ${Math.ceil(100 / m)}vw, (max-width: ${WIDEST_PAGE}px) ${Math.ceil(100 / c)}vw, ${cap}px`
}

/** Start an above-the-fold image's request before its <img> exists. The link
 *  carries the <img>'s own srcset and sizes, so the browser picks the same
 *  candidate and the <img> is served from that response. Returns the link so the
 *  caller can remove it when the page goes. */
export function preloadImage(href: string, srcset: string | undefined, sizes: string): HTMLLinkElement {
  const link = document.createElement("link")
  link.rel = "preload"
  link.as = "image"
  link.href = href
  if (srcset) {
    link.setAttribute("imagesrcset", srcset)
    link.setAttribute("imagesizes", sizes)
  }
  link.setAttribute("fetchpriority", "high")
  document.head.appendChild(link)
  return link
}
